import Phaser from 'phaser';
import { Player } from '../entities/Player';
import { WeaponSystem } from './WeaponSystem';
import { Upgrade, UpgradeType } from '../types/Upgrades';
import { GameConfig } from '../config/GameConfig';

export class UpgradeSystem {
  private scene: Phaser.Scene;
  private player: Player;
  private weaponSystem: WeaponSystem;

  // Track current stats
  public damageMultiplier: number = 1;
  public attackSpeedMultiplier: number = 1;
  public moveSpeedMultiplier: number = 1;
  public magnetRange: number = 100;

  // Track unlocked weapons
  private unlockedWeapons: Set<string> = new Set();

  // Track weapon upgrade levels
  private weaponLevels: Map<string, {
    damage: number;
    speed: number;
    projectiles: number;
    other: number;
  }> = new Map();

  constructor(scene: Phaser.Scene, player: Player, weaponSystem: WeaponSystem) {
    this.scene = scene;
    this.player = player;
    this.weaponSystem = weaponSystem;
  }

  private getWeaponLevel(weapon: string, stat: 'damage' | 'speed' | 'projectiles' | 'other'): number {
    if (!this.weaponLevels.has(weapon)) {
      this.weaponLevels.set(weapon, { damage: 0, speed: 0, projectiles: 0, other: 0 });
    }
    return this.weaponLevels.get(weapon)![stat];
  }

  private incrementWeaponLevel(weapon: string, stat: 'damage' | 'speed' | 'projectiles' | 'other'): void {
    if (!this.weaponLevels.has(weapon)) {
      this.weaponLevels.set(weapon, { damage: 0, speed: 0, projectiles: 0, other: 0 });
    }
    this.weaponLevels.get(weapon)![stat]++;
  }

  public generateRandomUpgrades(count: number = 3): Upgrade[] {
    const allUpgrades = this.getAllPossibleUpgrades();

    // Guarantee yarn ball weapon appears at level 2 if not already unlocked
    if (this.player.level === 2 && !this.unlockedWeapons.has('yarn_ball')) {
      const yarnBallUpgrade = allUpgrades.find(u => u.id === 'yarn_ball');
      const otherUpgrades = allUpgrades.filter(u => u.id !== 'yarn_ball');
      const shuffled = Phaser.Utils.Array.Shuffle([...otherUpgrades]);

      if (yarnBallUpgrade) {
        return [yarnBallUpgrade, ...shuffled.slice(0, count - 1)];
      }
    }

    const shuffled = Phaser.Utils.Array.Shuffle([...allUpgrades]);
    return shuffled.slice(0, Math.min(count, shuffled.length));
  }

  private getAllPossibleUpgrades(): Upgrade[] {
    const upgrades: Upgrade[] = [
      {
        id: UpgradeType.DAMAGE,
        name: '+20% Damage',
        description: 'Increase all weapon damage by 20%',
        icon: '💪',
        apply: () => {
          this.damageMultiplier += 0.2;
          this.weaponSystem.updateDamageMultiplier(this.damageMultiplier);
          // Update all other weapons
          if (this.unlockedWeapons.has('yarn_ball')) {
            (this.scene as any).yarnBallWeapon?.updateDamageMultiplier(this.damageMultiplier);
          }
          if (this.unlockedWeapons.has('laser_pointer')) {
            (this.scene as any).laserPointerWeapon?.updateDamageMultiplier(this.damageMultiplier);
          }
          if (this.unlockedWeapons.has('fish_weapon')) {
            (this.scene as any).fishWeapon?.updateDamageMultiplier(this.damageMultiplier);
          }
          if (this.unlockedWeapons.has('catnip_bomb')) {
            (this.scene as any).catnipBombWeapon?.updateDamageMultiplier(this.damageMultiplier);
          }
        }
      },
      {
        id: UpgradeType.ATTACK_SPEED,
        name: '+15% Attack Speed',
        description: 'Attack faster with your claws',
        icon: '⚡',
        apply: () => {
          this.attackSpeedMultiplier += 0.15;
          this.weaponSystem.updateAttackSpeed(this.attackSpeedMultiplier);
        }
      },
      {
        id: UpgradeType.MOVE_SPEED,
        name: '+15% Move Speed',
        description: 'Run faster to dodge enemies',
        icon: '👟',
        apply: () => {
          this.moveSpeedMultiplier += 0.15;
          const newSpeed = GameConfig.PLAYER.SPEED * (1 + (this.moveSpeedMultiplier - 1));
          this.player.setData('moveSpeed', newSpeed);
        }
      },
      {
        id: UpgradeType.MAX_HEALTH,
        name: '+20 Max Health',
        description: 'Increase maximum health',
        icon: '❤️',
        apply: () => {
          this.player.maxHealth += 20;
          this.player.health += 20;
        }
      },
      {
        id: UpgradeType.HEAL,
        name: 'Heal 50 HP',
        description: 'Restore 50 health points',
        icon: '💚',
        apply: () => {
          this.player.health = Math.min(this.player.maxHealth, this.player.health + 50);
        }
      },
      {
        id: UpgradeType.PROJECTILE_SPEED,
        name: '+25% Projectile Speed',
        description: 'Claws fly faster',
        icon: '🚀',
        apply: () => {
          this.weaponSystem.updateProjectileSpeed(1.25);
        }
      },
      {
        id: UpgradeType.MAGNET_RANGE,
        name: '+50% Pickup Range',
        description: 'Collect XP gems from farther away',
        icon: '🧲',
        apply: () => {
          this.magnetRange *= 1.5;
        }
      }
    ];

    // Add weapon unlocks if not yet unlocked
    if (!this.unlockedWeapons.has('yarn_ball')) {
      upgrades.push({
        id: 'yarn_ball',
        name: 'Yarn Ball Attack',
        description: 'Unlock bouncing yarn balls that fire in 4 directions',
        icon: '🧶',
        apply: () => {
          this.unlockedWeapons.add('yarn_ball');
          (this.scene as any).enableYarnBallWeapon();
        }
      });
    }

    // Laser pointer disabled - causing performance issues
    // if (!this.unlockedWeapons.has('laser_pointer')) {
    //   upgrades.push({
    //     id: 'laser_pointer',
    //     name: 'Laser Pointer',
    //     description: 'Shoots fast laser projectiles in rotating patterns',
    //     icon: '🔴',
    //     apply: () => {
    //       this.unlockedWeapons.add('laser_pointer');
    //       (this.scene as any).enableLaserPointerWeapon();
    //     }
    //   });
    // }

    if (!this.unlockedWeapons.has('fish_weapon')) {
      upgrades.push({
        id: 'fish_weapon',
        name: 'Flying Fish',
        description: 'Unlock homing fish projectiles that seek enemies',
        icon: '🐟',
        apply: () => {
          this.unlockedWeapons.add('fish_weapon');
          (this.scene as any).enableFishWeapon();
        }
      });
    }

    if (!this.unlockedWeapons.has('catnip_bomb')) {
      upgrades.push({
        id: 'catnip_bomb',
        name: 'Catnip Bomb',
        description: 'Explosive catnip bombs with area damage',
        icon: '💣',
        apply: () => {
          this.unlockedWeapons.add('catnip_bomb');
          (this.scene as any).enableCatnipBombWeapon();
        }
      });
    }

    if (!this.unlockedWeapons.has('cat_collar')) {
      upgrades.push({
        id: 'cat_collar',
        name: 'Golden Collar',
        description: 'Unlock a magic collar that damages nearby enemies',
        icon: '🔔',
        apply: () => {
          this.unlockedWeapons.add('cat_collar');
          (this.scene as any).enableCatCollarWeapon();
        }
      });
    }

    if (!this.unlockedWeapons.has('milk_splash')) {
      upgrades.push({
        id: 'milk_splash',
        name: 'Milk Splash',
        description: 'Create damaging milk puddles that linger on the ground',
        icon: '🥛',
        apply: () => {
          this.unlockedWeapons.add('milk_splash');
          (this.scene as any).enableMilkSplashWeapon();
        }
      });
    }

    if (!this.unlockedWeapons.has('scratching_post')) {
      upgrades.push({
        id: 'scratching_post',
        name: 'Scratching Posts',
        description: 'Orbiting scratching posts that damage enemies on contact',
        icon: '🪵',
        apply: () => {
          this.unlockedWeapons.add('scratching_post');
          (this.scene as any).enableScratchingPostWeapon();
        }
      });
    }

    // Add weapon-specific upgrades for unlocked weapons
    this.addWeaponUpgrades(upgrades);

    return upgrades;
  }

  private addWeaponUpgrades(upgrades: Upgrade[]): void {
    // Claw weapon upgrades (always available)
    const clawDamageLevel = this.getWeaponLevel('claw', 'damage');
    if (clawDamageLevel < 5) {
      upgrades.push({
        id: 'claw_damage',
        name: 'Sharper Claws',
        description: `+25% claw damage (Level ${clawDamageLevel + 1})`,
        icon: '🗡️',
        apply: () => {
          this.incrementWeaponLevel('claw', 'damage');
          this.weaponSystem.updateDamageMultiplier(this.damageMultiplier + 0.25);
        }
      });
    }

    const clawSpeedLevel = this.getWeaponLevel('claw', 'speed');
    if (clawSpeedLevel < 5) {
      upgrades.push({
        id: 'claw_speed',
        name: 'Faster Claws',
        description: `+20% claw attack speed (Level ${clawSpeedLevel + 1})`,
        icon: '⚡',
        apply: () => {
          this.incrementWeaponLevel('claw', 'speed');
          const newSpeed = this.attackSpeedMultiplier + 0.2;
          this.attackSpeedMultiplier = newSpeed;
          this.weaponSystem.updateAttackSpeed(newSpeed);
        }
      });
    }

    const clawCountLevel = this.getWeaponLevel('claw', 'projectiles');
    if (clawCountLevel < 3) {
      upgrades.push({
        id: 'claw_count',
        name: 'More Claws',
        description: `+1 additional claw projectile (Level ${clawCountLevel + 1})`,
        icon: '➕',
        apply: () => {
          this.incrementWeaponLevel('claw', 'projectiles');
          this.weaponSystem.addProjectile();
        }
      });
    }

    // Yarn Ball upgrades
    if (this.unlockedWeapons.has('yarn_ball')) {
      const yarnDamageLevel = this.getWeaponLevel('yarn_ball', 'damage');
      if (yarnDamageLevel < 5) {
        upgrades.push({
          id: 'yarn_damage',
          name: 'Denser Yarn',
          description: `+30% yarn ball damage (Level ${yarnDamageLevel + 1})`,
          icon: '🧶',
          apply: () => {
            this.incrementWeaponLevel('yarn_ball', 'damage');
            (this.scene as any).yarnBallWeapon?.upgradeDamage(0.3);
          }
        });
      }

      const yarnCountLevel = this.getWeaponLevel('yarn_ball', 'projectiles');
      if (yarnCountLevel < 2) {
        upgrades.push({
          id: 'yarn_count',
          name: 'More Yarn Balls',
          description: `+2 additional yarn directions (Level ${yarnCountLevel + 1})`,
          icon: '🧶',
          apply: () => {
            this.incrementWeaponLevel('yarn_ball', 'projectiles');
            (this.scene as any).yarnBallWeapon?.addDirections();
          }
        });
      }

      const yarnBounceLevel = this.getWeaponLevel('yarn_ball', 'other');
      if (yarnBounceLevel < 3) {
        upgrades.push({
          id: 'yarn_bounce',
          name: 'Bouncier Yarn',
          description: `+2 additional bounces (Level ${yarnBounceLevel + 1})`,
          icon: '⚾',
          apply: () => {
            this.incrementWeaponLevel('yarn_ball', 'other');
            (this.scene as any).yarnBallWeapon?.addBounces(2);
          }
        });
      }
    }

    // Fish weapon upgrades
    if (this.unlockedWeapons.has('fish_weapon')) {
      const fishDamageLevel = this.getWeaponLevel('fish_weapon', 'damage');
      if (fishDamageLevel < 5) {
        upgrades.push({
          id: 'fish_damage',
          name: 'Bigger Fish',
          description: `+30% fish damage (Level ${fishDamageLevel + 1})`,
          icon: '🐟',
          apply: () => {
            this.incrementWeaponLevel('fish_weapon', 'damage');
            (this.scene as any).fishWeapon?.upgradeDamage(0.3);
          }
        });
      }

      const fishSpeedLevel = this.getWeaponLevel('fish_weapon', 'speed');
      if (fishSpeedLevel < 3) {
        upgrades.push({
          id: 'fish_speed',
          name: 'Faster Fish',
          description: `Fish spawn ${fishSpeedLevel === 0 ? '33%' : '25%'} faster (Level ${fishSpeedLevel + 1})`,
          icon: '💨',
          apply: () => {
            this.incrementWeaponLevel('fish_weapon', 'speed');
            (this.scene as any).fishWeapon?.upgradeSpeed();
          }
        });
      }

      const fishCountLevel = this.getWeaponLevel('fish_weapon', 'projectiles');
      if (fishCountLevel < 2) {
        upgrades.push({
          id: 'fish_count',
          name: 'More Fish',
          description: `+1 fish per attack (Level ${fishCountLevel + 1})`,
          icon: '🐟',
          apply: () => {
            this.incrementWeaponLevel('fish_weapon', 'projectiles');
            (this.scene as any).fishWeapon?.addFish();
          }
        });
      }
    }

    // Laser pointer upgrades
    if (this.unlockedWeapons.has('laser_pointer')) {
      const laserDamageLevel = this.getWeaponLevel('laser_pointer', 'damage');
      if (laserDamageLevel < 5) {
        upgrades.push({
          id: 'laser_damage',
          name: 'Stronger Laser',
          description: `+35% laser damage (Level ${laserDamageLevel + 1})`,
          icon: '🔴',
          apply: () => {
            this.incrementWeaponLevel('laser_pointer', 'damage');
            (this.scene as any).laserPointerWeapon?.upgradeDamage(0.35);
          }
        });
      }

      const laserSpeedLevel = this.getWeaponLevel('laser_pointer', 'speed');
      if (laserSpeedLevel < 3) {
        upgrades.push({
          id: 'laser_speed',
          name: 'Faster Firing',
          description: `Laser shoots 15% faster (Level ${laserSpeedLevel + 1})`,
          icon: '🔄',
          apply: () => {
            this.incrementWeaponLevel('laser_pointer', 'speed');
            (this.scene as any).laserPointerWeapon?.upgradeSpeed();
          }
        });
      }

      const laserCountLevel = this.getWeaponLevel('laser_pointer', 'projectiles');
      if (laserCountLevel < 2) {
        upgrades.push({
          id: 'laser_count',
          name: 'Multi-Laser',
          description: `+1 additional laser beam (Level ${laserCountLevel + 1})`,
          icon: '🔴',
          apply: () => {
            this.incrementWeaponLevel('laser_pointer', 'projectiles');
            (this.scene as any).laserPointerWeapon?.addBeam();
          }
        });
      }
    }

    // Catnip bomb upgrades
    if (this.unlockedWeapons.has('catnip_bomb')) {
      const bombDamageLevel = this.getWeaponLevel('catnip_bomb', 'damage');
      if (bombDamageLevel < 5) {
        upgrades.push({
          id: 'bomb_damage',
          name: 'Stronger Bombs',
          description: `+35% bomb damage (Level ${bombDamageLevel + 1})`,
          icon: '💣',
          apply: () => {
            this.incrementWeaponLevel('catnip_bomb', 'damage');
            (this.scene as any).catnipBombWeapon?.upgradeDamage(0.35);
          }
        });
      }

      const bombSpeedLevel = this.getWeaponLevel('catnip_bomb', 'speed');
      if (bombSpeedLevel < 3) {
        upgrades.push({
          id: 'bomb_speed',
          name: 'Faster Bombs',
          description: `Bombs spawn ${bombSpeedLevel === 0 ? '25%' : '20%'} faster (Level ${bombSpeedLevel + 1})`,
          icon: '⏱️',
          apply: () => {
            this.incrementWeaponLevel('catnip_bomb', 'speed');
            (this.scene as any).catnipBombWeapon?.upgradeSpeed();
          }
        });
      }

      const bombRadiusLevel = this.getWeaponLevel('catnip_bomb', 'other');
      if (bombRadiusLevel < 3) {
        upgrades.push({
          id: 'bomb_radius',
          name: 'Bigger Explosions',
          description: `+20% explosion radius (Level ${bombRadiusLevel + 1})`,
          icon: '💥',
          apply: () => {
            this.incrementWeaponLevel('catnip_bomb', 'other');
            (this.scene as any).catnipBombWeapon?.upgradeRadius(0.2);
          }
        });
      }
    }

    // Cat Collar Upgrades
    if (this.unlockedWeapons.has('cat_collar')) {
      const collarDamageLevel = this.getWeaponLevel('cat_collar', 'damage');
      if (collarDamageLevel < 5) {
        upgrades.push({
          id: 'collar_damage',
          name: 'Blessed Collar',
          description: `+40% aura damage (Level ${collarDamageLevel + 1})`,
          icon: '✨',
          apply: () => {
            this.incrementWeaponLevel('cat_collar', 'damage');
            (this.scene as any).catCollarWeapon?.upgradeDamage(0.4);
          }
        });
      }

      const collarRadiusLevel = this.getWeaponLevel('cat_collar', 'other');
      if (collarRadiusLevel < 4) {
        upgrades.push({
          id: 'collar_radius',
          name: 'Wider Aura',
          description: `+25 aura radius (Level ${collarRadiusLevel + 1})`,
          icon: '⭕',
          apply: () => {
            this.incrementWeaponLevel('cat_collar', 'other');
            (this.scene as any).catCollarWeapon?.upgradeRadius(25);
          }
        });
      }
    }

    // Milk Splash Upgrades
    if (this.unlockedWeapons.has('milk_splash')) {
      const milkDamageLevel = this.getWeaponLevel('milk_splash', 'damage');
      if (milkDamageLevel < 5) {
        upgrades.push({
          id: 'milk_damage',
          name: 'Spoiled Milk',
          description: `+30% milk damage (Level ${milkDamageLevel + 1})`,
          icon: '🥛',
          apply: () => {
            this.incrementWeaponLevel('milk_splash', 'damage');
            (this.scene as any).milkSplashWeapon?.upgradeDamage(0.3);
          }
        });
      }

      const milkSpeedLevel = this.getWeaponLevel('milk_splash', 'speed');
      if (milkSpeedLevel < 3) {
        upgrades.push({
          id: 'milk_speed',
          name: 'Faster Splashing',
          description: `Puddles spawn 15% faster (Level ${milkSpeedLevel + 1})`,
          icon: '💨',
          apply: () => {
            this.incrementWeaponLevel('milk_splash', 'speed');
            (this.scene as any).milkSplashWeapon?.upgradeSpeed();
          }
        });
      }

      const milkCountLevel = this.getWeaponLevel('milk_splash', 'projectiles');
      if (milkCountLevel < 2) {
        upgrades.push({
          id: 'milk_count',
          name: 'More Puddles',
          description: `+1 milk puddle per attack (Level ${milkCountLevel + 1})`,
          icon: '🥛',
          apply: () => {
            this.incrementWeaponLevel('milk_splash', 'projectiles');
            (this.scene as any).milkSplashWeapon?.addPuddle();
          }
        });
      }
    }

    // Scratching Post Upgrades
    if (this.unlockedWeapons.has('scratching_post')) {
      const postDamageLevel = this.getWeaponLevel('scratching_post', 'damage');
      if (postDamageLevel < 5) {
        upgrades.push({
          id: 'post_damage',
          name: 'Sharper Posts',
          description: `+35% scratching post damage (Level ${postDamageLevel + 1})`,
          icon: '🪵',
          apply: () => {
            this.incrementWeaponLevel('scratching_post', 'damage');
            (this.scene as any).scratchingPostWeapon?.upgradeDamage(0.35);
          }
        });
      }

      const postSpeedLevel = this.getWeaponLevel('scratching_post', 'speed');
      if (postSpeedLevel < 3) {
        upgrades.push({
          id: 'post_speed',
          name: 'Faster Rotation',
          description: `Posts orbit 25% faster (Level ${postSpeedLevel + 1})`,
          icon: '🌀',
          apply: () => {
            this.incrementWeaponLevel('scratching_post', 'speed');
            (this.scene as any).scratchingPostWeapon?.upgradeSpeed(0.25);
          }
        });
      }

      const postCountLevel = this.getWeaponLevel('scratching_post', 'projectiles');
      if (postCountLevel < 2) {
        upgrades.push({
          id: 'post_count',
          name: 'More Posts',
          description: `+1 orbiting post (Level ${postCountLevel + 1})`,
          icon: '🪵',
          apply: () => {
            this.incrementWeaponLevel('scratching_post', 'projectiles');
            (this.scene as any).scratchingPostWeapon?.addPost();
          }
        });
      }

      const postRadiusLevel = this.getWeaponLevel('scratching_post', 'other');
      if (postRadiusLevel < 3) {
        upgrades.push({
          id: 'post_radius',
          name: 'Wider Orbit',
          description: `+15 orbit radius (Level ${postRadiusLevel + 1})`,
          icon: '⭕',
          apply: () => {
            this.incrementWeaponLevel('scratching_post', 'other');
            (this.scene as any).scratchingPostWeapon?.upgradeRadius(15);
          }
        });
      }
    }
  }

  public applyUpgrade(upgrade: Upgrade): void {
    upgrade.apply();
  }
}
